import { first } from 'lodash'

import { dateBackendToUI } from 'modules/core/lib/dates'
import formatCurrency from 'modules/core/lib/formatCurrency'
import i18n from 'modules/core/lib/i18n'
import type { Company } from 'modules/core/store/models/Company'

import type IncidenceChanges from 'modules/bookkeepers/store/collections/changes'
import { FieldObject, IncidenceDetailsField } from 'modules/payroll_bundle/lib/types'
import {
  booleanModifier,
  frequencyModifier,
  translatedCotizationGroup,
  weekdaysModifier,
  workingHoursModifier,
} from 'modules/payroll_bundle/lib/utils'

export type NewDetail = Record<string, string>
export type OldDetail = Record<string, string> | undefined
export type DetailProps = OldDetail | NewDetail

const renderSalaryAmount = (amount: number, company: Company | null, legalEntityId?: number) => {
  return formatCurrency(amount, '', company, legalEntityId)
}

const renderEndDate = (date: string | null) => {
  if (date === null) {
    return i18n.t('bookkeepers.incidence.details.contract_end_date_undefined')
  } else {
    return dateBackendToUI(date)
  }
}

export const hasUpdate = (
  oldIncidenceDetail: OldDetail,
  newIncidenceDetail: NewDetail,
  field: string
) => {
  /**
   * If there's no old incidence then we have a newly created incidence
   * and we want to show it
   */
  if (!oldIncidenceDetail) return true
  return oldIncidenceDetail && oldIncidenceDetail[field] !== newIncidenceDetail[field]
}

export const hasAnyFieldUpdate = (
  oldIncidenceDetail: OldDetail,
  newIncidenceDetail: NewDetail,
  fieldList: IncidenceDetailsField[]
): boolean => {
  return fieldList.some(({ field }) => hasUpdate(oldIncidenceDetail, newIncidenceDetail, field))
}

export const getOldIncidenceDetail = (incidenceChangesCollection: IncidenceChanges): OldDetail => {
  /**
   * If there's only one item in the collection it means there are no updates
   */
  if (incidenceChangesCollection.length > 1) {
    return first(
      incidenceChangesCollection
        .toJS()
        .sort(
          (a, b): number =>
            a.original_contract_version_id - b.original_contract_version_id || a.id - b.id
        )
    )
  }

  return undefined
}

const displayDateBackendToUI = ({ fieldValue: value }: FieldObject) => {
  if (value === undefined) return undefined
  if (value === null) return null
  if (typeof value === 'number' || typeof value === 'boolean') return null

  return dateBackendToUI(value)
}

const displayEndDate = ({ fieldValue: value }: FieldObject) => {
  if (value === undefined) return undefined
  if (value === null) return null
  if (typeof value === 'number' || typeof value === 'boolean') return null

  return renderEndDate(value)
}

const displayWorkingHours = ({ fieldValue: value, extraValue: units, company }: FieldObject) => {
  if (value === undefined || units === undefined) return undefined
  if (value === null || units === null) return null

  return workingHoursModifier({ fieldValue: value, extraValue: units, company })
}

const displayBooleanValue = ({ fieldValue: value, company }: FieldObject) => {
  if (value === undefined) return undefined
  if (value === null) return null

  return booleanModifier({ fieldValue: value, company })
}

const displayWeekdays = ({ fieldValue: value, company }: FieldObject) => {
  if (value === undefined) return undefined
  if (value === null) return null

  return weekdaysModifier({ fieldValue: value, company })
}

const displayCotizationGroup = ({ fieldValue: id }: FieldObject) => {
  if (id === undefined) return undefined
  if (id === null) return null
  if (typeof id === 'number' || typeof id === 'boolean') return null

  return translatedCotizationGroup(id)
}

const displaySalaryAmount = ({ fieldValue: value, company, legalEntityId }: FieldObject) => {
  if (value === undefined) return undefined
  if (value === null) return null

  return renderSalaryAmount(Number(value), company, legalEntityId)
}

const displaySalaryFrequency = ({ fieldValue: value, company }: FieldObject) => {
  if (value === undefined) return undefined
  if (value === null) return null

  return frequencyModifier({ fieldValue: value, company })
}

export const BasicFields: IncidenceDetailsField[] = [
  {
    field: 'job_title',
    translationKey: 'job_title',
  },
  {
    field: 'job_catalog_level_name',
    translationKey: 'role_level',
  },
  {
    field: 'effective_on',
    translationKey: 'effective_on',
    valueModifier: displayDateBackendToUI,
  },
  {
    field: 'ends_on',
    translationKey: 'contract_end_date',
    valueModifier: displayEndDate,
  },
  {
    field: 'es_contract_type',
    translationKey: 'contract_type',
  },
  {
    field: 'es_working_day',
    translationKey: 'working_day',
  },
  {
    field: 'fr_contract_type_name',
    translationKey: 'contract_type',
  },
  {
    field: 'fr_work_type_name',
    translationKey: 'work_type',
  },
]

export const ExtraFields: IncidenceDetailsField[] = [
  {
    field: 'es_job_description',
    translationKey: 'job_description',
  },
  {
    field: 'es_education_level',
    translationKey: 'education_level',
  },
  {
    field: 'es_professional_category_name',
    translationKey: 'professional_category',
  },
  {
    field: 'es_cotization_group',
    translationKey: 'cotization_group',
    valueModifier: displayCotizationGroup,
  },
  {
    field: 'es_has_teleworking_contract',
    translationKey: 'has_teleworking_contract',
    valueModifier: displayBooleanValue,
  },
  {
    field: 'es_contract_observations',
    translationKey: 'contract_observations',
  },
  {
    field: 'es_contribution_type',
    translationKey: 'contribution_type',
  },
  {
    field: 'es_agreement_code',
    translationKey: 'agreement_code',
  },
  {
    field: 'es_cno_occupation',
    translationKey: 'cno_occupation',
  },
  {
    field: 'es_regime',
    translationKey: 'regime',
  },
  {
    field: 'es_tariff_group',
    translationKey: 'tariff_group',
  },
  {
    field: 'es_occupation_code',
    translationKey: 'occupation_code',
  },
  {
    field: 'es_classification',
    translationKey: 'classification',
  },
  {
    field: 'es_a3innuva_job_position',
    translationKey: 'a3innuva_job_position',
  },
  {
    field: 'fr_professional_category_name',
    translationKey: 'fr_professional_category',
  },
  {
    field: 'fr_mutual_name',
    translationKey: 'mutual',
  },
  {
    field: 'de_personal_key_group_name',
    translationKey: 'de_personal_key_group_name',
  },
]

export const HoursFields: IncidenceDetailsField[] = [
  {
    field: 'working_hours',
    extraField: 'working_hours_frequency',
    translationKey: 'working_hours',
    valueModifier: displayWorkingHours,
  },

  {
    field: 'working_week_days',
    translationKey: 'working_week_days',
    valueModifier: displayWeekdays,
  },
]

export const SalaryFields: IncidenceDetailsField[] = [
  {
    field: 'salary_amount',
    translationKey: 'salary_amount',
    valueModifier: displaySalaryAmount,
  },
  {
    field: 'salary_frequency',
    translationKey: 'salary_frequency',
    valueModifier: displaySalaryFrequency,
  },
  {
    field: 'fr_coefficient',
    translationKey: 'coefficient',
  },
]
